import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import {
  deactivateAppraisalRetrospective,
  deactivateAppraisalRetrospectiveGroup,
  getAppraisalsRetrospectives,
  getAppraisalRetrospectiveGroups,
  saveAppraisalRetrospective,
  saveAppraisalRetrospectiveGroup,
} from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import { APPRAISAL_LIMITS, clampLen, validateFields } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import "./Appraisal.css";

const AppraisalRetrospectivePage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [retros, setRetros] = useState([]);
  const [groups, setGroups] = useState([]);
  const [helpKey, setHelpKey] = useState(null);
  const [retroForm, setRetroForm] = useState({ name: "", description: "", sortOrder: 0 });
  const [groupForm, setGroupForm] = useState({ name: "", description: "", retrospectiveIds: [] });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  /** @type {[null | 'retro' | 'group', Function]} */
  const [saving, setSaving] = useState(null);

  const canConfigureGroups = retros.length > 0;

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [r, g] = await Promise.all([getAppraisalsRetrospectives(), getAppraisalRetrospectiveGroups()]);
      setRetros(r || []);
      setGroups(g || []);
    } catch (e) {
      setError(e?.message || "Failed to load retrospectives");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSaveRetro = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const err = validateFields([
      { value: retroForm.name, required: true, max: APPRAISAL_LIMITS.name, label: "Name" },
      { value: retroForm.description, max: APPRAISAL_LIMITS.description, label: "Description" },
    ]);
    if (err) {
      setError(err);
      return;
    }
    setSaving("retro");
    try {
      await saveAppraisalRetrospective({ ...retroForm, isActive: true });
      setRetroForm({ name: "", description: "", sortOrder: 0 });
      setMessage(
        t("app.appraisal.retro.saved", {
          defaultValue: "Retrospective saved.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save retrospective");
    } finally {
      setSaving(null);
    }
  };

  const onSaveGroup = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const err = validateFields([
      { value: groupForm.name, required: true, max: APPRAISAL_LIMITS.name, label: "Name" },
      { value: groupForm.description, max: APPRAISAL_LIMITS.description, label: "Description" },
    ]);
    if (err) {
      setError(err);
      return;
    }
    setSaving("group");
    try {
      await saveAppraisalRetrospectiveGroup({ ...groupForm, isActive: true });
      setGroupForm({ name: "", description: "", retrospectiveIds: [] });
      setMessage(
        t("app.appraisal.retro.savedGroup", {
          defaultValue: "Group saved.",
        })
      );
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save group");
    } finally {
      setSaving(null);
    }
  };

  const toggleRetroInGroup = (id) => {
    setGroupForm((prev) => {
      const has = prev.retrospectiveIds.includes(id);
      return {
        ...prev,
        retrospectiveIds: has
          ? prev.retrospectiveIds.filter((x) => x !== id)
          : [...prev.retrospectiveIds, id],
      };
    });
  };

  const openDeleteConfirm = (type, item) => {
    if (type === "retro") {
      setDeleteConfirm({
        type,
        id: item.id,
        title: t("app.appraisal.retro.deleteTitle", { defaultValue: "Delete retrospective" }),
        body: t("app.appraisal.retro.deleteBody", {
          defaultValue: "Remove “{{name}}”? It will no longer appear on new appraisals.",
          name: item.name,
        }),
        name: item.name,
      });
      return;
    }
    setDeleteConfirm({
      type,
      id: item.id,
      title: t("app.appraisal.retro.deleteGroupTitle", { defaultValue: "Delete retrospective group" }),
      body: t("app.appraisal.retro.deleteGroupBody", {
        defaultValue:
          "Remove group “{{name}}”? Linked statements stay available; only the package is removed.",
        name: item.name,
      }),
      name: item.name,
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    setError("");
    try {
      if (deleteConfirm.type === "retro") await deactivateAppraisalRetrospective(deleteConfirm.id);
      else await deactivateAppraisalRetrospectiveGroup(deleteConfirm.id);
      setDeleteConfirm(null);
      setMessage(t("app.appraisal.deleted", { defaultValue: "Deleted." }));
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to delete");
      setDeleteConfirm(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="appraisal-page appraisal-retro-page">
      <CommonPageHeader
        icon={<i className="bi bi-arrow-counterclockwise" />}
        title={t("app.appraisal.retroTitle", { defaultValue: "Retrospectives" })}
        description={t("app.appraisal.retroSubtitle", {
          defaultValue: "Define reflection statements and package them into groups for appraisals.",
        })}
        rightContent={
          <AppraisalGuideButton
            onClick={() => setHelpKey("retrospective")}
            label={t("app.appraisal.retro.guideBtn", {
              defaultValue: "How retrospectives work",
            })}
          />
        }
      />
      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!loading && (
        <div className="appraisal-framework-layout">
          <div className="appraisal-framework-setup">
            <section className="appraisal-settings-panel appraisal-framework-panel">
              <div className="appraisal-settings-panel__head">
                <AppraisalSectionHeader
                  icon="bi-chat-quote"
                  title={t("app.appraisal.retro.addTitle", {
                    defaultValue: "Reflection statements",
                  })}
                  helpKey="retrospective"
                  onOpenHelp={setHelpKey}
                  tone="retrospective"
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.retro.hint", {
                    defaultValue:
                      "Reflection questions about the past period — what went well, what blocked progress, or what to improve next.",
                  })}
                </p>
              </div>

              <form onSubmit={onSaveRetro} className="appraisal-framework-form">
                <label className="appraisal-field">
                  {t("app.appraisal.retro.name", { defaultValue: "Name" })}
                  <input
                    required
                    maxLength={APPRAISAL_LIMITS.name}
                    placeholder={t("app.appraisal.retro.namePlaceholder", {
                      defaultValue: "e.g. Reduced tech debt on the auth service",
                    })}
                    value={retroForm.name}
                    onChange={(e) =>
                      setRetroForm((p) => ({ ...p, name: clampLen(e.target.value, APPRAISAL_LIMITS.name) }))
                    }
                  />
                  <AppraisalCharCount value={retroForm.name} max={APPRAISAL_LIMITS.name} />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.retro.description", { defaultValue: "Description" })}
                  <textarea
                    rows={3}
                    maxLength={APPRAISAL_LIMITS.description}
                    placeholder={t("app.appraisal.retro.descPlaceholder", {
                      defaultValue: "What should people reflect on?",
                    })}
                    value={retroForm.description}
                    onChange={(e) =>
                      setRetroForm((p) => ({
                        ...p,
                        description: clampLen(e.target.value, APPRAISAL_LIMITS.description),
                      }))
                    }
                  />
                  <AppraisalCharCount value={retroForm.description} max={APPRAISAL_LIMITS.description} />
                </label>
                <div className="appraisal-framework-form__actions">
                  <button type="submit" className="appraisal-btn primary" disabled={!!saving}>
                    {saving === "retro"
                      ? t("app.common.saving", { defaultValue: "Saving…" })
                      : t("app.appraisal.retro.addStatement", {
                          defaultValue: "Add statement",
                        })}
                  </button>
                </div>
              </form>

              <div className="appraisal-framework-list-block">
                <div className="appraisal-framework-list-block__head">
                  <span>
                    {t("app.appraisal.retro.listTitle", {
                      defaultValue: "Added statements",
                    })}
                  </span>
                  <span className="appraisal-framework-count">{retros.length}</span>
                </div>
                {retros.length === 0 ? (
                  <div className="appraisal-framework-empty">
                    <i className="bi bi-chat-quote" aria-hidden />
                    <p>
                      {t("app.appraisal.retro.empty", {
                        defaultValue:
                          "No reflection statements yet. Add your first question above.",
                      })}
                    </p>
                  </div>
                ) : (
                  <ul className="appraisal-framework-items">
                    {retros.map((r) => (
                      <li key={r.id} className="appraisal-framework-item">
                        <div className="appraisal-framework-item__body">
                          <strong>{r.name}</strong>
                          {r.description ? <span>{r.description}</span> : null}
                        </div>
                        <button
                          type="button"
                          className="appraisal-list-delete"
                          onClick={() => openDeleteConfirm("retro", r)}
                          title={t("app.common.delete", { defaultValue: "Delete" })}
                        >
                          <i className="bi bi-trash3" aria-hidden />
                          <span>{t("app.common.delete", { defaultValue: "Delete" })}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {!canConfigureGroups ? (
              <section className="appraisal-settings-panel appraisal-framework-locked">
                <div className="appraisal-framework-locked__icon" aria-hidden>
                  <i className="bi bi-folder2-open" />
                </div>
                <div className="appraisal-framework-locked__copy">
                  <strong>
                    {t("app.appraisal.retro.groupsLockedTitle", {
                      defaultValue: "Groups come next",
                    })}
                  </strong>
                  <p>
                    {t("app.appraisal.retro.groupsLockedBody", {
                      defaultValue:
                        "Add at least one reflection statement first. Groups package those statements for different teams or roles.",
                    })}
                  </p>
                  <ul className="appraisal-framework-checklist">
                    <li>
                      <i className="bi bi-circle" aria-hidden />
                      <span>
                        {t("app.appraisal.retro.groupsNeedStatement", {
                          defaultValue: "At least one reflection statement",
                        })}
                      </span>
                    </li>
                  </ul>
                </div>
              </section>
            ) : (
              <section className="appraisal-settings-panel appraisal-framework-panel">
                <div className="appraisal-settings-panel__head">
                  <AppraisalSectionHeader
                    icon="bi-folder2-open"
                    title={t("app.appraisal.retro.groupTitle", {
                      defaultValue: "Retrospective groups",
                    })}
                    helpKey="retrospectiveGroups"
                    onOpenHelp={setHelpKey}
                    tone="retrospectiveGroups"
                  />
                  <p className="appraisal-framework-hint">
                    {t("app.appraisal.retro.groupHint", {
                      defaultValue:
                        "Package reflection questions for teams or roles — e.g. Engineering Retros for backend/frontend.",
                    })}
                  </p>
                </div>

                <form onSubmit={onSaveGroup} className="appraisal-framework-form">
                  <label className="appraisal-field">
                    {t("app.appraisal.retro.groupName", { defaultValue: "Name" })}
                    <input
                      required
                      maxLength={APPRAISAL_LIMITS.name}
                      placeholder={t("app.appraisal.retro.groupNamePlaceholder", {
                        defaultValue: "e.g. Software Engineering Retros",
                      })}
                      value={groupForm.name}
                      onChange={(e) =>
                        setGroupForm((p) => ({ ...p, name: clampLen(e.target.value, APPRAISAL_LIMITS.name) }))
                      }
                    />
                    <AppraisalCharCount value={groupForm.name} max={APPRAISAL_LIMITS.name} />
                  </label>
                  <label className="appraisal-field">
                    {t("app.appraisal.retro.groupDesc", { defaultValue: "Description" })}
                    <textarea
                      rows={2}
                      maxLength={APPRAISAL_LIMITS.description}
                      placeholder={t("app.appraisal.retro.groupDescPlaceholder", {
                        defaultValue: "Who this package is for",
                      })}
                      value={groupForm.description}
                      onChange={(e) =>
                        setGroupForm((p) => ({
                          ...p,
                          description: clampLen(e.target.value, APPRAISAL_LIMITS.description),
                        }))
                      }
                    />
                    <AppraisalCharCount value={groupForm.description} max={APPRAISAL_LIMITS.description} />
                  </label>

                  <div className="appraisal-field">
                    <span className="appraisal-retro-link-label">
                      {t("app.appraisal.retro.linkStatements", {
                        defaultValue: "Link statements",
                      })}
                    </span>
                    <div className="appraisal-retro-link-grid">
                      {retros.map((r) => {
                        const checked = groupForm.retrospectiveIds.includes(r.id);
                        return (
                          <label
                            key={r.id}
                            className={`appraisal-retro-link-chip${checked ? " is-on" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRetroInGroup(r.id)}
                            />
                            <i className={`bi ${checked ? "bi-check-circle-fill" : "bi-circle"}`} aria-hidden />
                            <span>{r.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="appraisal-framework-form__actions">
                    <button type="submit" className="appraisal-btn primary" disabled={!!saving}>
                      {saving === "group"
                        ? t("app.common.saving", { defaultValue: "Saving…" })
                        : t("app.appraisal.retro.addGroup", {
                            defaultValue: "Add group",
                          })}
                    </button>
                  </div>
                </form>

                <div className="appraisal-framework-list-block">
                  <div className="appraisal-framework-list-block__head">
                    <span>
                      {t("app.appraisal.retro.groupsListTitle", {
                        defaultValue: "Added groups",
                      })}
                    </span>
                    <span className="appraisal-framework-count">{groups.length}</span>
                  </div>
                  {groups.length === 0 ? (
                    <div className="appraisal-framework-empty">
                      <i className="bi bi-folder2-open" aria-hidden />
                      <p>
                        {t("app.appraisal.retro.groupsEmpty", {
                          defaultValue:
                            "No groups yet. Name a package, tick statements, then save.",
                        })}
                      </p>
                    </div>
                  ) : (
                    <ul className="appraisal-framework-items">
                      {groups.map((g) => (
                        <li key={g.id} className="appraisal-framework-item">
                          <div className="appraisal-framework-item__body">
                            <strong>{g.name}</strong>
                            {g.description ? <span>{g.description}</span> : null}
                            <span className="appraisal-framework-item__meta">
                              {t("app.appraisal.retro.linkedCount", {
                                defaultValue: "{{count}} linked statement(s)",
                                count: (g.retrospectiveIds || []).length,
                              })}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="appraisal-list-delete"
                            onClick={() => openDeleteConfirm("group", g)}
                            title={t("app.common.delete", { defaultValue: "Delete" })}
                          >
                            <i className="bi bi-trash3" aria-hidden />
                            <span>{t("app.common.delete", { defaultValue: "Delete" })}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            )}
          </div>

          <section className="appraisal-feature-callout">
            <i className="bi bi-lightbulb" aria-hidden />
            <div>
              <strong>
                {t("app.appraisal.retro.howItFitsTitle", {
                  defaultValue: "How this fits appraisals",
                })}
              </strong>
              <p>
                {t("app.appraisal.retro.howItFitsBody", {
                  defaultValue:
                    "Enable Retrospective in Settings before starting a cycle. Staff, managers, and meetings then rate these reflections alongside Behaviors. Do not change the Retro flag mid-cycle.",
                })}
              </p>
            </div>
          </section>
        </div>
      )}

      {deleteConfirm ? (
        <div
          className="appraisal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="appraisal-retro-delete-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteLoading) setDeleteConfirm(null);
          }}
        >
          <div className="appraisal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appraisal-modal__header">
              <div>
                <p className="appraisal-modal__eyebrow">
                  {t("app.appraisal.retroTitle", { defaultValue: "Retrospectives" })}
                </p>
                <h2 className="appraisal-modal__title" id="appraisal-retro-delete-title">
                  {deleteConfirm.title}
                </h2>
              </div>
              <button
                type="button"
                className="appraisal-btn"
                disabled={deleteLoading}
                onClick={() => setDeleteConfirm(null)}
              >
                {t("app.common.close", { defaultValue: "Close" })}
              </button>
            </div>
            <div className="appraisal-modal__body">
              <div className="appraisal-modal__summary">
                <div className="appraisal-modal__icon" aria-hidden>
                  <i className="bi bi-trash3" />
                </div>
                <p className="appraisal-modal__lead">{deleteConfirm.body}</p>
              </div>
              {deleteConfirm.name ? (
                <div className="appraisal-modal__target">
                  <p className="appraisal-modal__target-label">
                    {t("app.appraisal.retro.deleteTarget", { defaultValue: "Item" })}
                  </p>
                  <p className="appraisal-modal__target-name">{deleteConfirm.name}</p>
                </div>
              ) : null}
              <div className="appraisal-modal__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={deleteLoading}
                  onClick={() => setDeleteConfirm(null)}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  className="appraisal-btn danger"
                  disabled={deleteLoading}
                  onClick={confirmDelete}
                >
                  {deleteLoading
                    ? t("app.common.deleting", { defaultValue: "Deleting…" })
                    : t("app.common.delete", { defaultValue: "Delete" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalRetrospectivePage;
